(() => {
  if (window.NamSubscriptionSelector) {
    window.NamSubscriptionSelector.init();
    return;
  }

  const selector = '[data-nam-subscription-config]';
  const variableNames = [
    '--nam-sub-heading', '--nam-sub-primary', '--nam-sub-secondary', '--nam-sub-card-bg',
    '--nam-sub-card-selected-bg', '--nam-sub-card-border', '--nam-sub-card-selected-border',
    '--nam-sub-card-border-width', '--nam-sub-card-selected-width', '--nam-sub-card-radius',
    '--nam-sub-card-gap', '--nam-sub-card-padding', '--nam-sub-card-padding-mobile', '--nam-sub-radio-border',
    '--nam-sub-radio-selected', '--nam-sub-radio-size', '--nam-sub-badge-bg',
    '--nam-sub-badge-text', '--nam-sub-badge-border', '--nam-sub-badge-radius',
    '--nam-sub-flag-1-bg', '--nam-sub-flag-1-text', '--nam-sub-flag-1-border',
    '--nam-sub-flag-1-width', '--nam-sub-flag-1-radius', '--nam-sub-flag-2-bg',
    '--nam-sub-flag-2-text', '--nam-sub-flag-2-border', '--nam-sub-flag-2-width',
    '--nam-sub-flag-2-radius', '--nam-sub-check', '--nam-sub-benefit',
    '--nam-sub-button-bg', '--nam-sub-button-text', '--nam-sub-button-hover-bg',
    '--nam-sub-button-hover-text', '--nam-sub-button-border', '--nam-sub-button-border-width',
    '--nam-sub-button-radius', '--nam-product-accent', '--nam-product-accent-hover'
  ];

  const copyVariables = (source, target) => {
    const styles = getComputedStyle(source);
    variableNames.forEach((name) => {
      const value = styles.getPropertyValue(name);
      if (value.trim()) target.style.setProperty(name, value);
    });
  };

  const getPricing = (config) => {
    try {
      return JSON.parse(config.querySelector('[data-nam-subscription-pricing]').textContent);
    } catch (error) {
      return { currency: 'USD', variants: [] };
    }
  };

  const money = (cents, currency) => {
    const amount = Number(cents || 0) / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: currency || 'USD'
      }).format(amount);
    } catch (error) {
      return '$' + amount.toFixed(2);
    }
  };

  const ensurePurchaseInner = (scope, productHandle) => {
    let purchaseInner = scope.querySelector(':scope > .nam-purchase-inner');
    const appBlock = [...scope.querySelectorAll('.shopify-app-block')]
      .find((item) => item.querySelector('.shopify_subscriptions_app_container'));
    const buyButtons = scope.querySelector('.buy-buttons-block');
    const accordion = scope.querySelector('.nam-acc');
    const purchaseItems = [appBlock, buyButtons].filter(Boolean);
    const allItems = [...purchaseItems, accordion].filter(Boolean);

    if (!allItems.length) return null;

    if (!purchaseInner) {
      purchaseInner = document.createElement('div');
      purchaseInner.className = 'nam-purchase-inner';
      const firstItem = allItems.find((item) => item.parentElement === scope);
      scope.insertBefore(purchaseInner, firstItem || null);
    }

    let purchaseControls = purchaseInner.querySelector(':scope > .nam-purchase-controls-inner');
    if (!purchaseControls) {
      purchaseControls = document.createElement('div');
      purchaseControls.className = 'nam-purchase-controls-inner';
      purchaseInner.insertBefore(purchaseControls, purchaseInner.firstChild);
    }

    if (productHandle) {
      purchaseInner.dataset.productHandle = productHandle;
      purchaseInner.classList.add('nam-purchase-inner--' + productHandle);
      purchaseControls.dataset.productHandle = productHandle;
      purchaseControls.classList.add('nam-purchase-controls-inner--' + productHandle);
    }

    purchaseItems.forEach((item) => {
      if (item.parentElement !== purchaseControls) purchaseControls.append(item);
    });

    if (accordion && accordion.parentElement !== purchaseInner) {
      purchaseInner.append(accordion);
    }

    return { purchaseInner, purchaseControls };
  };

  const enhance = (config) => {
    if (!config || !config.isConnected) return;
    const scope = config.parentElement;
    if (!scope) return;

    const appBlock = [...scope.querySelectorAll('.shopify-app-block')]
      .find((item) => item.querySelector('.shopify_subscriptions_app_container'));
    const buyButtons = scope.querySelector('.buy-buttons-block');
    const flagConfig = scope.querySelector('[data-nam-subscription-flags]') || config;
    const pricing = getPricing(config);
    const purchaseLayout = ensurePurchaseInner(scope, config.dataset.productHandle);
    const purchaseInner = purchaseLayout?.purchaseInner;
    const purchaseControls = purchaseLayout?.purchaseControls;

    if (purchaseInner) copyVariables(config, purchaseInner);
    if (purchaseControls) copyVariables(config, purchaseControls);

    if (buyButtons) {
      buyButtons.classList.add('nam-subscription-purchase');
      buyButtons.classList.toggle('show-dynamic-checkout', config.dataset.showDynamicCheckout === 'true');
      copyVariables(config, buyButtons);
    }

    if (!appBlock) return;
    const planInputs = [...appBlock.querySelectorAll('input[data-radio-type="selling_plan"]')];
    if (!planInputs.length) return;

    appBlock.classList.add('nam-subscription-selector');
    copyVariables(config, appBlock);
    copyVariables(flagConfig, appBlock);

    const appSection = [...appBlock.querySelectorAll('.shopify_subscriptions_app_block')]
      .find((item) => !item.classList.contains('shopify_subscriptions_app_block--hidden'))
      || appBlock.querySelector('.shopify_subscriptions_app_block');
    if (!appSection) return;

    if (!appSection.querySelector('.nam-subscription-heading')) {
      const heading = document.createElement('h2');
      heading.className = 'nam-subscription-heading';
      heading.textContent = config.dataset.heading || 'Subscribe & Save';
      appSection.prepend(heading);
    }

    const variantInput = scope.querySelector('product-form-component input[name="id"]');
    const quantityInput = scope.querySelector('product-form-component input[name="quantity"]');
    const variantId = Number(variantInput?.value || appSection.dataset.variantId || 0);
    const variant = pricing.variants.find((item) => Number(item.id) === variantId) || pricing.variants[0];
    const quantity = Math.max(1, Number(quantityInput?.value || 1));
    const groups = [...appSection.querySelectorAll('.plan_card > .subscription_group')];
    let subscriptionIndex = 0;

    const getSelectedSellingPlanId = () => {
      const currentAppSection = appBlock.querySelector('.shopify_subscriptions_app_block:not(.shopify_subscriptions_app_block--hidden)')
        || appBlock.querySelector('.shopify_subscriptions_app_block');
      return currentAppSection?.querySelector('input[data-radio-type="selling_plan"]:checked')?.dataset.sellingPlanId || '';
    };

    const syncSellingPlan = () => {
      const sellingPlanId = getSelectedSellingPlanId();
      scope.querySelectorAll('input[name="selling_plan"]').forEach((hiddenInput) => {
        hiddenInput.value = sellingPlanId;
        hiddenInput.defaultValue = sellingPlanId;
        if (sellingPlanId) hiddenInput.setAttribute('value', sellingPlanId);
        else hiddenInput.removeAttribute('value');
      });
    };

    const scheduleSellingPlanSync = () => {
      syncSellingPlan();
      requestAnimationFrame(() => {
        syncSellingPlan();
        requestAnimationFrame(syncSellingPlan);
      });
      [50, 150, 500, 1000].forEach((delay) => setTimeout(syncSellingPlan, delay));
    };

    groups.forEach((group) => {
      const input = group.querySelector('input[type="radio"]');
      if (!input) return;
      const isSubscription = input.dataset.radioType === 'selling_plan';
      group.classList.add('nam-subscription-option');
      group.classList.toggle('is-subscription', isSubscription);
      group.classList.toggle('nam-one-time', !isSubscription);

      if (isSubscription) {
        subscriptionIndex += 1;
        group.dataset.subscriptionIndex = String(subscriptionIndex);
        const allocation = variant?.allocations?.find((item) => Number(item.selling_plan_id) === Number(input.dataset.sellingPlanId));
        const price = Number(allocation?.price ?? 0);
        const compareAt = Number(allocation?.compare_at_price ?? variant?.price ?? price);
        const discount = compareAt > price ? Math.round(((compareAt - price) / compareAt) * 100) : 0;

        const titleWrapper = group.querySelector(':scope > .title_and_price_wrapper');
        if (titleWrapper && discount > 0 && !titleWrapper.querySelector('.nam-savings-badge')) {
          const badge = document.createElement('span');
          badge.className = 'nam-savings-badge';
          badge.textContent = `Save ${discount}%`;
          titleWrapper.append(badge);
        } else if (titleWrapper?.querySelector('.nam-savings-badge')) {
          titleWrapper.querySelector('.nam-savings-badge').textContent = `Save ${discount}%`;
        }

        let priceNode = group.querySelector('.nam-subscription-price');
        if (!priceNode) {
          priceNode = document.createElement('div');
          priceNode.className = 'nam-subscription-price';
          group.append(priceNode);
        }
        const total = price * quantity;
        const originalTotal = compareAt * quantity;
        priceNode.innerHTML = `
          <div class="nam-price-each">${money(price, pricing.currency)}<small>/each</small></div>
          <div class="nam-price-total">
            ${compareAt > price ? `<span class="nam-price-original">${money(originalTotal, pricing.currency)}</span>` : ''}
            <span>${money(total, pricing.currency)} total</span>
          </div>
        `;

        if (!group.querySelector('.nam-subscription-benefits')) {
          const template = config.querySelector('[data-nam-benefits-template]');
          if (template) group.append(template.content.cloneNode(true));
        }

        [1, 2].forEach((flagNumber) => {
          const enabled = flagConfig.getAttribute(`data-flag-${flagNumber}-enabled`) === 'true';
          const target = Number(flagConfig.getAttribute(`data-flag-${flagNumber}-target`) || 0);
          const text = flagConfig.getAttribute(`data-flag-${flagNumber}-text`) || '';
          let flag = group.querySelector(`.nam-subscription-flag--${flagNumber}`);
          if (enabled && target === subscriptionIndex && text) {
            if (!flag) {
              flag = document.createElement('span');
              flag.className = `nam-subscription-flag nam-subscription-flag--${flagNumber}`;
              group.append(flag);
            }
            flag.textContent = text;
          } else {
            flag?.remove();
          }
        });
        group.classList.toggle('has-flag', Boolean(group.querySelector('.nam-subscription-flag')));
      } else {
        const label = group.querySelector('.one_time_purchase_option_app_block > span');
        label?.classList.add('nam-one-time-label');
        let priceNode = group.querySelector('.nam-subscription-price');
        if (!priceNode) {
          priceNode = document.createElement('div');
          priceNode.className = 'nam-subscription-price';
          group.append(priceNode);
        }
        priceNode.innerHTML = `<div class="nam-price-each">${money((variant?.price || 0) * quantity, pricing.currency)}<small>/each</small></div>`;
      }

      const refresh = () => {
        groups.forEach((item) => {
          const radio = item.querySelector('input[type="radio"]');
          const selected = Boolean(radio?.checked);
          item.classList.toggle('is-selected', selected);
          item.setAttribute('aria-expanded', item.classList.contains('is-subscription') && selected ? 'true' : 'false');
        });
      };

      if (input.dataset.namSubscriptionBound !== 'true') {
        input.dataset.namSubscriptionBound = 'true';
        input.addEventListener('change', () => {
          scheduleSellingPlanSync();
          requestAnimationFrame(refresh);
        });
      }
      if (group.dataset.namSubscriptionBound !== 'true') {
        group.dataset.namSubscriptionBound = 'true';
        group.addEventListener('click', (event) => {
          if (event.target.closest('a, button, input, select')) return;
          input.click();
        });
      }
      refresh();
    });
    scheduleSellingPlanSync();

    scope.querySelectorAll('form[action*="/cart/add"]').forEach((form) => {
      if (form.dataset.namSubscriptionSubmitBound === 'true') return;
      form.dataset.namSubscriptionSubmitBound = 'true';
      form.addEventListener('pointerdown', syncSellingPlan, true);
      form.addEventListener('click', syncSellingPlan, true);
      form.addEventListener('submit', syncSellingPlan, true);
      form.addEventListener('formdata', (event) => {
        const sellingPlanId = getSelectedSellingPlanId();
        if (sellingPlanId) event.formData.set('selling_plan', sellingPlanId);
        else event.formData.delete('selling_plan');
      });
    });

    if (quantityInput && quantityInput.dataset.namSubscriptionBound !== 'true') {
      quantityInput.dataset.namSubscriptionBound = 'true';
      quantityInput.addEventListener('change', () => enhance(config));
      quantityInput.addEventListener('input', () => enhance(config));
    }

    if (scope.dataset.namSubscriptionObserved !== 'true') {
      scope.dataset.namSubscriptionObserved = 'true';
      let scheduled = false;
      new MutationObserver((mutations) => {
        const appContentChanged = mutations.some((mutation) =>
          [...mutation.addedNodes].some((node) =>
            node.nodeType === 1 && (
              node.matches?.('.shopify_subscriptions_app_block, .shopify_subscriptions_app_container, input[name="selling_plan"]')
              || node.querySelector?.('.shopify_subscriptions_app_block, .shopify_subscriptions_app_container, input[name="selling_plan"]')
            )
          )
        );
        if (!appContentChanged) return;
        if (scheduled) return;
        scheduled = true;
        requestAnimationFrame(() => {
          scheduled = false;
          const currentConfig = scope.querySelector(selector);
          if (currentConfig) enhance(currentConfig);
        });
      }).observe(scope, { childList: true, subtree: true });
    }
  };

  const init = (root = document) => root.querySelectorAll(selector).forEach(enhance);
  window.NamSubscriptionSelector = { init };
  init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:block:load', (event) => init(event.target.closest('section') || document));
})();
